import { useState, useEffect } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Date presets
export const DATE_PRESETS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_14_DAYS: "last_14_days",
  LAST_30_DAYS: "last_30_days",
  LAST_90_DAYS: "last_90_days",
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_QUARTER: "this_quarter",
  LAST_QUARTER: "last_quarter",
  THIS_YEAR: "this_year",
  LAST_YEAR: "last_year",
  CUSTOM: "custom",
};

// Get date range from preset
export const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case DATE_PRESETS.TODAY:
      return { start: today, end: today, label: "Today" };
    
    case DATE_PRESETS.YESTERDAY:
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: yesterday, label: "Yesterday" };
    
    case DATE_PRESETS.LAST_7_DAYS:
      const last7 = new Date(today);
      last7.setDate(last7.getDate() - 7);
      return { start: last7, end: today, label: "Last 7 days" };
    
    case DATE_PRESETS.LAST_14_DAYS:
      const last14 = new Date(today);
      last14.setDate(last14.getDate() - 14);
      return { start: last14, end: today, label: "Last 14 days" };
    
    case DATE_PRESETS.LAST_30_DAYS:
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      return { start: last30, end: today, label: "Last 30 days" };
    
    case DATE_PRESETS.LAST_90_DAYS:
      const last90 = new Date(today);
      last90.setDate(last90.getDate() - 90);
      return { start: last90, end: today, label: "Last 90 days" };
    
    case DATE_PRESETS.THIS_MONTH:
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: thisMonthStart, end: today, label: "This month" };
    
    case DATE_PRESETS.LAST_MONTH:
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: lastMonthStart, end: lastMonthEnd, label: "Last month" };
    
    case DATE_PRESETS.THIS_QUARTER:
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const thisQuarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      return { start: thisQuarterStart, end: today, label: "This quarter" };
    
    case DATE_PRESETS.LAST_QUARTER:
      const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
      const lastQuarterStart = new Date(lastQuarterYear, lastQuarterMonth, 1);
      const lastQuarterEnd = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);
      return { start: lastQuarterStart, end: lastQuarterEnd, label: "Last quarter" };
    
    case DATE_PRESETS.THIS_YEAR:
      const thisYearStart = new Date(now.getFullYear(), 0, 1);
      return { start: thisYearStart, end: today, label: "This year" };
    
    case DATE_PRESETS.LAST_YEAR:
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
      return { start: lastYearStart, end: lastYearEnd, label: "Last year" };
    
    default:
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 30);
      return { start: defaultStart, end: today, label: "Last 30 days" };
  }
};

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Format date for API
export const formatDateForAPI = (date) => {
  return date.toISOString().split("T")[0];
};

// Page-level date filter component with persistence
const PageDateFilter = ({ 
  pageId, 
  defaultPreset = DATE_PRESETS.LAST_30_DAYS,
  onDateChange,
  showTimePeriodBadge = true,
  compact = false 
}) => {
  // Get stored preset for this page from localStorage
  const getStoredPreset = () => {
    try {
      const stored = localStorage.getItem(`jmap_date_${pageId}`);
      return stored || defaultPreset;
    } catch {
      return defaultPreset;
    }
  };

  const getStoredCustomDates = () => {
    try {
      const stored = localStorage.getItem(`jmap_custom_dates_${pageId}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const [preset, setPreset] = useState(getStoredPreset);
  const [dateRange, setDateRange] = useState(() => {
    const storedPreset = getStoredPreset();
    if (storedPreset === DATE_PRESETS.CUSTOM) {
      const customDates = getStoredCustomDates();
      if (customDates) {
        return {
          start: new Date(customDates.start),
          end: new Date(customDates.end),
          label: "Custom"
        };
      }
    }
    return getDateRangeFromPreset(storedPreset);
  });
  
  // Custom date picker state
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Call onDateChange on initial mount to set parent state
  useEffect(() => {
    if (onDateChange) {
      const storedPreset = getStoredPreset();
      let startDate, endDate, label;
      
      if (storedPreset === DATE_PRESETS.CUSTOM) {
        const customDates = getStoredCustomDates();
        if (customDates) {
          startDate = customDates.start;
          endDate = customDates.end;
          label = "Custom";
        } else {
          const range = getDateRangeFromPreset(defaultPreset);
          startDate = formatDateForAPI(range.start);
          endDate = formatDateForAPI(range.end);
          label = range.label;
        }
      } else {
        const range = getDateRangeFromPreset(storedPreset);
        startDate = formatDateForAPI(range.start);
        endDate = formatDateForAPI(range.end);
        label = range.label;
      }
      
      onDateChange({
        preset: storedPreset,
        startDate,
        endDate,
        label
      });
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update when preset changes (but not on mount)
  useEffect(() => {
    if (preset !== DATE_PRESETS.CUSTOM) {
      const range = getDateRangeFromPreset(preset);
      setDateRange(range);
      
      // Store in localStorage for this user/page
      try {
        localStorage.setItem(`jmap_date_${pageId}`, preset);
      } catch (e) {
        console.error("Could not save date preference", e);
      }
      
      // Notify parent component
      if (onDateChange) {
        onDateChange({
          preset,
          startDate: formatDateForAPI(range.start),
          endDate: formatDateForAPI(range.end),
          label: range.label,
        });
      }
    }
  }, [preset, pageId, onDateChange]);

  // Handle custom date selection
  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      if (start <= end) {
        const range = {
          start,
          end,
          label: "Custom"
        };
        setDateRange(range);
        setPreset(DATE_PRESETS.CUSTOM);
        
        // Store custom dates
        try {
          localStorage.setItem(`jmap_date_${pageId}`, DATE_PRESETS.CUSTOM);
          localStorage.setItem(`jmap_custom_dates_${pageId}`, JSON.stringify({
            start: customStartDate,
            end: customEndDate
          }));
        } catch (e) {
          console.error("Could not save custom date preference", e);
        }
        
        // Notify parent component
        if (onDateChange) {
          onDateChange({
            preset: DATE_PRESETS.CUSTOM,
            startDate: customStartDate,
            endDate: customEndDate,
            label: "Custom",
          });
        }
        
        setShowCustomPicker(false);
      }
    }
  };

  const openCustomPicker = () => {
    // Pre-populate with current range
    setCustomStartDate(formatDateForAPI(dateRange.start));
    setCustomEndDate(formatDateForAPI(dateRange.end));
    setShowCustomPicker(true);
  };

  const presetOptions = [
    { value: DATE_PRESETS.TODAY, label: "Today" },
    { value: DATE_PRESETS.YESTERDAY, label: "Yesterday" },
    { value: DATE_PRESETS.LAST_7_DAYS, label: "Last 7 days" },
    { value: DATE_PRESETS.LAST_14_DAYS, label: "Last 14 days" },
    { value: DATE_PRESETS.LAST_30_DAYS, label: "Last 30 days" },
    { value: DATE_PRESETS.LAST_90_DAYS, label: "Last 90 days" },
    { divider: true },
    { value: DATE_PRESETS.THIS_MONTH, label: "This month" },
    { value: DATE_PRESETS.LAST_MONTH, label: "Last month" },
    { value: DATE_PRESETS.THIS_QUARTER, label: "This quarter" },
    { value: DATE_PRESETS.LAST_QUARTER, label: "Last quarter" },
    { divider: true },
    { value: DATE_PRESETS.THIS_YEAR, label: "This year" },
    { value: DATE_PRESETS.LAST_YEAR, label: "Last year" },
    { divider: true },
    { value: DATE_PRESETS.CUSTOM, label: "Custom Range..." },
  ];

  return (
    <>
      <div className={`flex items-center gap-3 ${compact ? "" : "flex-wrap"}`}>
        {/* Time Period Badge */}
        {showTimePeriodBadge && (
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 py-1 px-3">
            <Calendar className="w-3 h-3 mr-1.5" />
            {formatDate(dateRange.start)} — {formatDate(dateRange.end)}
          </Badge>
        )}
        
        {/* Date Selector Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="gap-2 bg-white border-slate-200 hover:bg-slate-50"
            >
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="font-medium">{dateRange.label}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {presetOptions.map((option, index) => 
              option.divider ? (
                <DropdownMenuSeparator key={`divider-${index}`} />
              ) : option.value === DATE_PRESETS.CUSTOM ? (
                <DropdownMenuItem
                  key={option.value}
                  onClick={openCustomPicker}
                  className="text-blue-600 font-medium"
                >
                  {option.label}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setPreset(option.value)}
                  className={preset === option.value ? "bg-slate-100 font-medium" : ""}
                >
                  {option.label}
                </DropdownMenuItem>
              )
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Custom Date Picker Dialog */}
      <Dialog open={showCustomPicker} onOpenChange={setShowCustomPicker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            {customStartDate && customEndDate && new Date(customStartDate) > new Date(customEndDate) && (
              <p className="text-sm text-red-500">Start date must be before end date</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomPicker(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCustomDateSubmit}
              disabled={!customStartDate || !customEndDate || new Date(customStartDate) > new Date(customEndDate)}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PageDateFilter;
